import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';

const CashbookReportView = ({ reportData, fromDate, toDate, onBack }) => {
    const { t } = useLang();
    const [businessProfile, setBusinessProfile] = useState(null);
    useEffect(() => {
        const fetchBiz = async () => {
            try {
                const { getBusinessProfile } = await import('../../services/dashboardApi');
                const res = await getBusinessProfile();
                if (res?.success) setBusinessProfile(res.data);
            } catch (err) {
                console.error('Error fetching biz profile:', err);
            }
        };
        fetchBiz();
    }, []);

    const formatCurrency = (amount) => {
        const n = parseFloat(amount) || 0;
        return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handlePrint = () => {
        window.print();
    };

    // Extract all entries from daily summaries
    const allEntries = [];
    if (reportData?.daily_summaries) {
        reportData.daily_summaries.forEach(day => {
            if (day.entries) {
                day.entries.forEach(entry => {
                    allEntries.push({
                        ...entry,
                        displayDate: day.date
                    });
                });
            }
        });
    }
    allEntries.sort((a, b) => new Date(b.displayDate || b.entry_date) - new Date(a.displayDate || a.entry_date));

    return (
        <div className="print-main-wrapper" style={styles.container}>
            {/* Top Nav Bar */}
            <div style={styles.navBar} className="no-print">
                <div style={styles.navLeft}>
                    <button style={styles.backBtn} onClick={onBack}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h3 style={styles.navTitle}>{t(LangKeys.cashbook_report_title)}</h3>
                        <span style={styles.navSub}>{formatDate(fromDate)} to {formatDate(toDate)}</span>
                    </div>
                </div>
                <button style={styles.printBtn} onClick={handlePrint}>
                    <Download size={18} />
                    <span>{t(LangKeys.print_download)}</span>
                </button>
            </div>

            <div className="no-print" style={styles.scrollArea}>
                <div id="cashbook-printable-report" style={styles.reportSheet}>

                    <div className="header-row" style={styles.headerRow}>
                        <div className="side-logo">
                            {businessProfile?.logo_url && <img src={businessProfile.logo_url} style={styles.logoImg} />}
                        </div>
                        <div className="center-info">
                            <img src="https://khata.moxpal.com/assets/logo.png" style={styles.moxpalImg} alt="Moxpal" />
                            <h2 className="biz-name" style={styles.bizName}>{businessProfile?.business_name || ''}</h2>
                            {businessProfile?.owner_name && <div className="biz-owner" style={styles.bizOwner}>{t(LangKeys.name)} : {businessProfile.owner_name}</div>}
                            <div className="biz-contact" style={styles.bizContact}>{t(LangKeys.mobile_no)}: {businessProfile?.mobile || ''}</div>
                        </div>
                        <div className="side-logo">
                            <img src="https://khata.moxpal.com/assets/ganesh-ji.png" style={styles.ganeshImg} />
                        </div>
                    </div>

                    <div className="title-banner" style={styles.banner}>
                        {t(LangKeys.cashbook_report_title).toUpperCase()}
                    </div>

                    <div className="date-subtitle" style={styles.dateSub}>
                        ({formatDate(fromDate)} - {formatDate(toDate)})
                    </div>

                    <div className="summary-row" style={styles.summaryRow}>
                        <div className="sum-card" style={styles.sumCard}>
                            <span className="sum-label" style={styles.sumLabel}>{t(LangKeys.total_in)}</span>
                            <span className="sum-val" style={{ ...styles.sumVal, color: '#15803d' }}>{formatCurrency(reportData?.total_in)}</span>
                        </div>
                        <div className="sum-card" style={styles.sumCard}>
                            <span className="sum-label" style={styles.sumLabel}>{t(LangKeys.total_out)}</span>
                            <span className="sum-val" style={{ ...styles.sumVal, color: '#b91c1c' }}>{formatCurrency(reportData?.total_out)}</span>
                        </div>
                        <div className="sum-card" style={styles.sumCard}>
                            <span className="sum-label" style={styles.sumLabel}>{t(LangKeys.closing_balance)}</span>
                            <span className="sum-val" style={{ ...styles.sumVal, color: (reportData?.closing_balance || 0) >= 0 ? '#15803d' : '#b91c1c' }}>
                                {formatCurrency(reportData?.closing_balance)}
                            </span>
                        </div>
                    </div>

                    <div className="section-label" style={styles.sectionLabel}>All Entries</div>

                    {/* PROFESSIONAL TABLE WITH BORDERS */}
                    <table style={{ ...styles.table, tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '22%' }}>{t(LangKeys.date).toUpperCase()}</th>
                                <th style={{ ...styles.th, width: '38%' }}>{t(LangKeys.note).toUpperCase()}</th>
                                <th style={{ ...styles.th, textAlign: 'right', width: '20%' }}>{t(LangKeys.in_entry).toUpperCase()}</th>
                                <th style={{ ...styles.th, textAlign: 'right', width: '20%' }}>{t(LangKeys.out_entry).toUpperCase()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allEntries.length > 0 ? allEntries.map((entry, idx) => (
                                <tr key={idx}>
                                    <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(entry.displayDate || entry.entry_date)}</td>
                                    <td style={{ ...styles.td, wordBreak: 'break-word' }}>{entry.note || '—'}</td>
                                    <td style={{ ...styles.td, textAlign: 'right', color: '#15803d', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {(entry.type || entry.entry_type) === 'in' ? formatCurrency(entry.amount) : ''}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', color: '#b91c1c', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {(entry.type || entry.entry_type) === 'out' ? formatCurrency(entry.amount) : ''}
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="4" style={{ ...styles.td, textAlign: 'center', padding: '40px' }}>No entries found</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="footer-note" style={styles.footerNote}>
                        {t(LangKeys.generated_by)} | {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
                    </div>
                </div>
            </div>

            {/* Printable Report Sheet */}
            <div className="print-only" id="cashbook-pdf-template" style={{
                padding: '10mm 15mm',
                boxSizing: 'border-box',
                background: 'white',
                fontFamily: "'Outfit', 'Hind', sans-serif",
                color: '#1e293b',
                textAlign: 'left',
                width: '100%'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center' }}>
                        {businessProfile?.logo_url && <img src={businessProfile.logo_url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="" />}
                    </div>
                    <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img src="https://khata.moxpal.com/assets/logo.png" style={{ height: '22px', marginBottom: '6px' }} alt="Moxpal" />
                        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{businessProfile?.business_name || ''}</h2>
                        {businessProfile?.owner_name && <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>{t(LangKeys.name)}: {businessProfile.owner_name}</div>}
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{t(LangKeys.mobile_no)}: {businessProfile?.mobile || ''}</div>
                    </div>
                    <div style={{ width: '60px', height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                        <img src="https://khata.moxpal.com/assets/ganesh-ji.png" style={{ width: '40px', height: '40px', objectFit: 'contain' }} alt="Ganesh" />
                    </div>
                </div>

                <div style={{ background: '#1e40af', color: 'white', padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px', margin: '20px 0', borderRadius: '4px', textTransform: 'uppercase' }}>
                    {t(LangKeys.cashbook_report_title).toUpperCase()}
                </div>

                <div style={{ textAlign: 'center', fontSize: '12px', color: '#475569', marginBottom: '20px', fontWeight: '600' }}>
                    ({formatDate(fromDate)} - {formatDate(toDate)})
                </div>

                <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
                    <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
                        <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.total_in)}</span>
                        <span style={{ fontSize: '15px', fontWeight: '800', color: '#15803d' }}>{formatCurrency(reportData?.total_in)}</span>
                    </div>
                    <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
                        <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.total_out)}</span>
                        <span style={{ fontSize: '15px', fontWeight: '800', color: '#b91c1c' }}>{formatCurrency(reportData?.total_out)}</span>
                    </div>
                    <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
                        <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.closing_balance)}</span>
                        <span style={{ fontSize: '15px', fontWeight: '800', color: (reportData?.closing_balance || 0) >= 0 ? '#15803d' : '#b91c1c' }}>{formatCurrency(reportData?.closing_balance)}</span>
                    </div>
                </div>

                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px', borderLeft: '4px solid #1e40af', paddingLeft: '10px' }}>
                    All Entries
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1', tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '22%' }}>{t(LangKeys.date).toUpperCase()}</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '38%' }}>{t(LangKeys.note).toUpperCase()}</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '20%' }}>{t(LangKeys.in_entry).toUpperCase()}</th>
                            <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '20%' }}>{t(LangKeys.out_entry).toUpperCase()}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allEntries.length > 0 ? allEntries.map((entry, idx) => (
                            <tr key={idx}>
                                <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDate(entry.displayDate || entry.entry_date)}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', wordBreak: 'break-word' }}>{entry.note || '—'}</td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#15803d', fontWeight: '700', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {(entry.type || entry.entry_type) === 'in' ? formatCurrency(entry.amount) : ''}
                                </td>
                                <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#b91c1c', fontWeight: '700', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {(entry.type || entry.entry_type) === 'out' ? formatCurrency(entry.amount) : ''}
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '30px', fontSize: '11px', color: '#64748b', textAlign: 'center' }}>No entries found</td></tr>
                        )}
                    </tbody>
                </table>

                <div style={{ marginTop: '40px', borderTop: '1px solid #e2e8f0', paddingTop: '15px', textAlign: 'center', fontSize: '10px', color: '#94a3b8', fontWeight: '600' }}>
                    {t(LangKeys.generated_by)} | {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: { height: '100%', display: 'flex', flexDirection: 'column', background: '#F1F5F9', borderRadius: '16px', overflow: 'hidden' },
    navBar: { padding: '16px 24px', background: '#FFFFFF', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 },
    navLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
    backBtn: { width: '40px', height: '40px', borderRadius: '10px', background: '#F1F5F9', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569' },
    navTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
    navSub: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
    printBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '10px', background: '#1E40AF', color: '#FFFFFF', border: 'none', fontWeight: '700', cursor: 'pointer' },

    scrollArea: { flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' },
    reportSheet: { background: '#FFFFFF', padding: '40px', width: '100%', maxWidth: '950px', boxSizing: 'border-box', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: '4px' },

    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
    logoImg: { width: '50px', height: '50px', objectFit: 'contain' },
    ganeshImg: { width: '40px', height: '40px', objectFit: 'contain' },
    centerInfo: { textAlign: 'center', flex: 1 },
    moxpalImg: { width: '100px', marginBottom: '8px' },
    bizName: { fontSize: '22px', fontWeight: '800', color: '#1E293B', margin: 0 },
    bizOwner: { fontSize: '12px', color: '#475569', marginTop: '4px', fontWeight: '500' },
    bizContact: { fontSize: '11px', color: '#64748B', marginTop: '2px', fontWeight: '500' },

    banner: { background: '#1E40AF', color: '#FFFFFF', padding: '12px', textAlign: 'center', fontWeight: '700', fontSize: '17px', textTransform: 'uppercase', margin: '20px 0', borderRadius: '4px' },
    dateSub: { textAlign: 'center', fontSize: '12px', color: '#475569', marginBottom: '20px', fontWeight: '600' },

    summaryRow: { display: 'flex', gap: '15px', marginBottom: '30px' },
    sumCard: { flex: 1, border: '1px solid #E2E8F0', borderRadius: '8px', padding: '15px', textAlign: 'center' },
    sumLabel: { fontSize: '10px', color: '#64748B', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' },
    sumVal: { fontSize: '16px', fontWeight: '800' },

    sectionLabel: { fontSize: '15px', fontWeight: '800', color: '#0F172A', marginBottom: '15px', borderLeft: '5px solid #1E40AF', paddingLeft: '12px' },

    table: { width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1' },
    th: { border: '1px solid #cbd5e1', padding: '12px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left' },
    td: { border: '1px solid #cbd5e1', padding: '12px', fontSize: '12px', color: '#334155', fontWeight: '500' },

    footerNote: { marginTop: '50px', borderTop: '1px solid #F1F5F9', paddingTop: '20px', textAlign: 'center', fontSize: '11px', color: '#94A3B8', fontWeight: '600' }
};

export default CashbookReportView;
